import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { noop } from 'lodash-es';
import { Box, Button, ColumnsPanelTrigger, DataTable, DropdownMenu, DropdownMenuItem, FilterPickerGroup, Page, PageContentHeader, PageEmptyState, PageErrorState, Row, Spacing } from 'seagull';
import { ChevronDown, Download, Import } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useOpenCreateClientModal } from '~/components/CreateClientModal/hooks/useOpenCreateClientModal';
import { useOpenEditClientModal } from '~/components/EditClientModal/hooks/useOpenEditClientModal';
import TableFilters from '~/components/TableFilters/TableFilters';
import { routes } from '~/constants';
import { useClientSummaries } from '~/hooks/reactQuery/queries/useClientSummaries';
import { useSalesTaxConfig } from '~/hooks/reactQuery/queries/useSalesTaxConfig/useSalesTaxConfig';
import { useClientArchiveRestoreDelete } from '~/hooks/useClientArchiveRestoreDelete';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useTableColumnMenu } from '~/hooks/useTableColumnMenu';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { ClientDetailPage } from '~/pages/ClientDetailPage/ClientDetailPage';
import { useOpenImportClientsModal } from '~/pages/ImportClients/hooks/useOpenImportClientsModal';
import { ClientsBulkActions } from './ClientsBulkActions';
import { clientsFilterConfig, clientsFilterSelection } from './ClientsPage.filters';
import { clientInlineActions, clientMenuActions } from './ClientsPage.rowActions';
import { CLIENT_SUMMARIES_PAGE_SIZE, CLIENT_SUMMARY_DEFAULT_SORT, CLIENT_SUMMARY_COLUMN_NAMES, clientSummaryColumns, clientSummarySortOptions, toClientSummariesParams, toClientSummaryRows, toClientSummaryTableSort, withClientsStatus, withSavedClient, withoutClients } from './ClientsPage.table.utils';
import { ALL_CLIENT_STATUSES, ALL_CLIENT_TYPES, sanitizeFilterValues } from './ClientsPage.utils';
import { useClientBulkActions } from './hooks/useClientBulkActions';
import { useClientReferenceFieldsData } from './hooks/useClientReferenceFieldsData';
import { useClientSelection } from './hooks/useClientSelection';
import { useClientsReferenceFields } from './hooks/useClientsReferenceFields';
import { useClientSummariesRefresh } from './hooks/useClientSummariesRefresh';
import { useExportClientsCsv } from './hooks/useExportClientsCsv';
export const CLIENTS_SEARCH_DEBOUNCE_MS = 300;
export const GetClientsPageTID = (parentTestId)=>getTestIds(parentTestId);
const MOBILE_FIELDS = [
    {
        name: 'clientName'
    },
    {
        name: 'status',
        inline: true
    },
    {
        name: 'type'
    },
    {
        name: 'contacts'
    }
];
const MOBILE_FIELDS_WITH_TAX = [
    ...MOBILE_FIELDS,
    {
        name: 'tax'
    }
];
const NO_REFERENCE_FIELDS = {
    fields: [],
    values: {},
    refresh: noop
};
function ClientsListView(param) {
    let { searchDebounceMs, referenceFields } = param;
    const { ClientsPageTestIds: TID } = useMemo(()=>GetClientsPageTID(), []);
    const isMobileDevice = useIsMobileForHomePage();
    const navigate = useNavigate();
    const openCreateClient = useOpenCreateClientModal();
    const openEditClient = useOpenEditClientModal();
    const { exportingAll, exportingSelected, handleExportAll, handleExportSelected } = useExportClientsCsv();
    const openImportClients = useOpenImportClientsModal({
        onExportClients: handleExportAll
    });
    const { salesTaxSupported } = useSalesTaxConfig();
    const { t: clientTexts } = useLocaleKeys('client');
    const [mainColumn, ...columns] = useMemo(()=>clientSummaryColumns({
            texts: clientTexts.dashboard,
            statusTexts: clientTexts.statusLabels,
            showTax: salesTaxSupported
        }), [
        salesTaxSupported
    ]);
    const [page, setPage] = useState(1);
    const reference = useClientsReferenceFields({
        data: referenceFields !== null && referenceFields !== void 0 ? referenceFields : NO_REFERENCE_FIELDS,
        mainColumn,
        columns,
        onFiltersChange: ()=>setPage(1)
    });
    const withReferenceFields = referenceFields != null;
    const sortIds = withReferenceFields ? reference.sortIds : CLIENT_SUMMARY_COLUMN_NAMES;
    const { sort: urlSort, changeSort } = useSortFromUrl({
        defaultSort: CLIENT_SUMMARY_DEFAULT_SORT,
        sortIds
    });
    const { urlState: { search, status: rawStatusFilterValue, type: rawTypeFilterValue }, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        status: {
            defaultValue: [
                'active'
            ],
            multi: true
        },
        type: {
            defaultValue: ALL_CLIENT_TYPES,
            multi: true
        }
    });
    const statusFilterValue = useMemo(()=>sanitizeFilterValues(rawStatusFilterValue, ALL_CLIENT_STATUSES), [
        rawStatusFilterValue
    ]);
    const typeFilterValue = useMemo(()=>sanitizeFilterValues(rawTypeFilterValue, ALL_CLIENT_TYPES), [
        rawTypeFilterValue
    ]);
    const statusFilterOptions = useMemo(()=>[
            {
                value: 'active',
                label: clientTexts.dashboard.statusFilter.active()
            },
            {
                value: 'archived',
                label: clientTexts.dashboard.statusFilter.archived()
            }
        ], []);
    const typeFilterOptions = useMemo(()=>[
            {
                value: 'business',
                label: clientTexts.dashboard.typeFilter.business()
            },
            {
                value: 'individual',
                label: clientTexts.dashboard.typeFilter.individual()
            }
        ], []);
    const filterLabels = useMemo(()=>({
            status: clientTexts.dashboard.statusFilter.label(),
            type: clientTexts.dashboard.typeFilter.label()
        }), []);
    const filterConfig = useMemo(()=>clientsFilterConfig({
            statusFilterOptions,
            typeFilterOptions
        }), [
        statusFilterOptions,
        typeFilterOptions
    ]);
    const selectedFilters = useMemo(()=>({
            status: statusFilterValue.length === statusFilterOptions.length ? [] : statusFilterValue,
            type: typeFilterValue.length === typeFilterOptions.length ? [] : typeFilterValue
        }), [
        statusFilterValue,
        typeFilterValue,
        statusFilterOptions,
        typeFilterOptions
    ]);
    const sortOptions = useMemo(()=>clientSummarySortOptions(clientTexts.dashboard), []);
    const tableSort = useMemo(()=>toClientSummaryTableSort(urlSort, sortIds), [
        urlSort,
        sortIds
    ]);
    const params = useMemo(()=>toClientSummariesParams({
            sort: tableSort,
            search,
            statusFilter: statusFilterValue,
            typeFilter: typeFilterValue,
            page,
            referenceFilters: withReferenceFields ? reference.filters : undefined
        }), [
        tableSort,
        search,
        statusFilterValue,
        typeFilterValue,
        page,
        withReferenceFields,
        reference.filters
    ]);
    const { page: summaries, hasData, isLoading, error, refresh, optimisticUpdate } = useClientSummaries(params, {
        refreshOnHookMount: true
    });
    useClientSummariesRefresh();
    const { selectedIds, numSelected, onSelectionChange, getSelectedIds, clearSelection, unselect, hasSharedClientSelected } = useClientSelection(summaries.clients);
    const { onArchive, onRestore, onDelete } = useClientArchiveRestoreDelete({
        onComplete: (param)=>{
            let { clientId, status } = param;
            return optimisticUpdate(withClientsStatus([
                clientId
            ], status));
        },
        onDeleteComplete: (clientId)=>{
            unselect(clientId);
            optimisticUpdate(withoutClients([
                clientId
            ]));
        }
    });
    const rows = useMemo(()=>toClientSummaryRows({
            clients: summaries.clients,
            texts: clientTexts.dashboard,
            statusTexts: clientTexts.statusLabels,
            referenceCells: withReferenceFields ? reference.cells : undefined
        }), [
        summaries.clients,
        withReferenceFields,
        reference.cells
    ]);
    const { onBulkExport, onBulkArchive, onBulkDelete } = useClientBulkActions({
        texts: clientTexts.dashboard.bulkDeleteConfirmation,
        getSelectedIds,
        onExportSelected: handleExportSelected,
        onComplete: (param)=>{
            let { actionType, succeededIds } = param;
            clearSelection();
            optimisticUpdate(actionType === 'archive' ? withClientsStatus(succeededIds, 'archived') : withoutClients(succeededIds));
        }
    });
    const columnMenu = useTableColumnMenu('clients');
    const mobileFields = salesTaxSupported ? MOBILE_FIELDS_WITH_TAX : MOBILE_FIELDS;
    const handleRefresh = ()=>{
        var _ref;
        ((_ref = referenceFields === null || referenceFields === void 0 ? void 0 : referenceFields.refresh) !== null && _ref !== void 0 ? _ref : noop)();
        return refresh();
    };
    const handleViewClient = (client)=>navigate(routes.client(client.id));
    const handleEditClient = (client)=>openEditClient({
            clientId: client.id,
            initialName: client.name,
            initialClientType: client.type,
            onSaved: (saved)=>optimisticUpdate(withSavedClient(client.id, saved))
        });
    const handleTableFiltersRefresh = (filters, sort)=>{
        changeUrlState(clientsFilterSelection({
            filters,
            statusFilterOptions
        }));
        if (sort !== undefined) changeSort(sort);
        setPage(1);
    };
    const handleSearchChange = (value)=>{
        changeUrlState({
            search: value
        });
        setPage(1);
    };
    const handleSortChange = (nextSort)=>{
        changeSort({
            colIndex: sortIds.indexOf(nextSort.name),
            sort: nextSort.direction
        });
        setPage(1);
    };
    const handleRowClick = (row)=>handleViewClient(row.client);
    const onClearFilters = ()=>{
        changeUrlState({
            search: '',
            status: ALL_CLIENT_STATUSES,
            type: ALL_CLIENT_TYPES
        });
        (withReferenceFields ? reference.clearFilters : noop)();
        setPage(1);
    };
    const tableFiltersProps = {
        testIdPrefix: TID.Filters(),
        onRefresh: handleTableFiltersRefresh,
        search: {
            bi: {
                name: 'clients_search'
            },
            value: search,
            testId: TID.Search(),
            debounceTime: searchDebounceMs,
            onChange: (event)=>handleSearchChange(event.target.value),
            onClear: ()=>handleSearchChange('')
        },
        mobile: {
            sortOptions,
            sort: urlSort
        }
    };
    const bulkActions = /*#__PURE__*/ _jsx(ClientsBulkActions, {
        testId: TID.BulkActions(),
        numSelected: numSelected,
        hasSharedClientSelected: hasSharedClientSelected,
        onArchive: onBulkArchive,
        onDelete: onBulkDelete,
        onExport: onBulkExport,
        exporting: exportingSelected
    });
    if (error && !hasData) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: TID.ErrorState(),
            title: clientTexts.dashboard.title(),
            onRefresh: handleRefresh,
            pullToRefresh: true
        });
    }
    return /*#__PURE__*/ _jsxs(Page, {
        testId: TID.Root(),
        variant: "containedHeight",
        onRefresh: handleRefresh,
        header: clientTexts.dashboard.title(),
        actions: /*#__PURE__*/ _jsxs(Row, {
            gap: Spacing.Space8,
            children: [
                /*#__PURE__*/ _jsxs(DropdownMenu, {
                    testId: TID.MoreActions.Root(),
                    target: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'clients_more_actions'
                        },
                        skin: "primaryOutline",
                        testId: TID.MoreActions.Button(),
                        suffixIcon: /*#__PURE__*/ _jsx(ChevronDown, {}),
                        children: clientTexts.dashboard.moreActions()
                    }),
                    children: [
                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            bi: {
                                name: 'import_clients'
                            },
                            iconStart: /*#__PURE__*/ _jsx(Download, {}),
                            testId: TID.MoreActions.ImportItem(),
                            onClick: openImportClients,
                            children: clientTexts.import.entryButton()
                        }),
                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            intent: Intent.ExportClientsToCsv,
                            bi: {
                                name: 'clients_export_all_csv'
                            },
                            iconStart: /*#__PURE__*/ _jsx(Import, {}),
                            testId: TID.MoreActions.ExportAllItem(),
                            onClick: handleExportAll,
                            disabled: exportingAll,
                            children: clientTexts.dashboard.exportClients()
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'create_client'
                    },
                    testId: TID.CreateButton(),
                    onClick: ()=>openCreateClient(),
                    children: clientTexts.dashboard.createClient()
                })
            ]
        }),
        children: [
            /*#__PURE__*/ _jsx(PageContentHeader, {
                children: withReferenceFields ? /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "start",
                    justifyContent: "space-between",
                    gap: Spacing.Space16,
                    children: [
                        /*#__PURE__*/ _jsx(Box, {
                            grow: true,
                            children: /*#__PURE__*/ _jsx(TableFilters, _object_spread_props(_object_spread({}, tableFiltersProps), {
                                filterLabels: _object_spread({}, filterLabels, reference.filterLabels),
                                filterConfig: _object_spread({}, filterConfig, reference.filterConfig),
                                filterGroups: reference.filterGroups,
                                hideSelectedFromAddFilter: true,
                                formatFilterLabel: reference.formatFilterLabel,
                                mobile: _object_spread_props(_object_spread({}, tableFiltersProps.mobile), {
                                    formatFilterDescription: reference.formatFilterDescription
                                }),
                                selectedFilters: _object_spread({}, selectedFilters, reference.selectedFilters),
                                setSelectedFilters: reference.changeFilters
                            }))
                        }),
                        !isMobileDevice && /*#__PURE__*/ _jsxs(Row, {
                            testId: TID.FilterActions(),
                            alignItems: "center",
                            gap: Spacing.Space8,
                            children: [
                                bulkActions,
                                /*#__PURE__*/ _jsx(ColumnsPanelTrigger, {
                                    testId: TID.ColumnsTrigger(),
                                    columnsPanel: reference.columnsPanel
                                })
                            ]
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                    children: [
                        /*#__PURE__*/ _jsx(TableFilters, _object_spread_props(_object_spread({}, tableFiltersProps), {
                            filterLabels: filterLabels,
                            filterConfig: filterConfig,
                            selectedFilters: selectedFilters,
                            setSelectedFilters: noop
                        })),
                        !isMobileDevice && bulkActions
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(DataTable, _object_spread_props(_object_spread({
                mainColumn: mainColumn,
                allowColumnResize: true,
                allowColumnReorder: true,
                reorderBi: {
                    name: 'clients_reorder_columns'
                },
                allowColumnHide: true
            }, withReferenceFields ? {
                allowColumnHeaderControls: true,
                columnsState: reference.layout,
                onColumnsStateChange: reference.changeLayout
            } : {
                columnsState: undefined,
                onColumnsStateChange: undefined
            }), {
                columnMenu: columnMenu,
                columns: withReferenceFields ? reference.columns : columns,
                items: rows,
                state: isLoading ? 'loading' : summaries.totalCount === 0 ? 'empty' : 'default',
                getRowId: (row)=>row.id,
                mobile: {
                    fields: withReferenceFields ? [
                        ...mobileFields,
                        ...reference.mobileFields
                    ] : mobileFields
                },
                onRowClick: handleRowClick,
                sort: {
                    name: tableSort.name,
                    direction: tableSort.direction,
                    sortedBy: 'consumer',
                    onChange: handleSortChange
                },
                selection: isMobileDevice ? undefined : {
                    selectedIds,
                    onChange: onSelectionChange
                },
                rowActions: {
                    menuBi: {
                        name: 'clients_actions'
                    },
                    menuTitle: (row)=>row.clientName,
                    menuSubtitle: (row)=>row.type,
                    inline: (row)=>clientInlineActions({
                            client: row.client,
                            texts: clientTexts.dashboard,
                            onViewClient: handleViewClient
                        }),
                    menu: (row)=>clientMenuActions({
                            client: row.client,
                            texts: clientTexts.dashboard,
                            eligibilityTooltipsTexts: clientTexts.dashboard.eligibilityTooltips,
                            onEditClient: handleEditClient,
                            onArchiveClient: onArchive,
                            onRestoreClient: onRestore,
                            onDeleteClient: onDelete
                        })
                },
                pagination: {
                    page,
                    pageSize: CLIENT_SUMMARIES_PAGE_SIZE,
                    total: summaries.totalCount,
                    onPageChange: setPage
                },
                emptyState: /*#__PURE__*/ _jsx(PageEmptyState, {
                    testId: TID.EmptyState(),
                    illustration: /*#__PURE__*/ _jsx(_Fragment, {}),
                    title: clientTexts.dashboard.emptyState.title(),
                    message: clientTexts.dashboard.emptyState.description(),
                    actions: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'clear_clients_filters'
                        },
                        testId: TID.ClearFiltersButton(),
                        onClick: onClearFilters,
                        children: clientTexts.dashboard.emptyState.clearFilters()
                    })
                }),
                testId: TID.Table()
            }))
        ]
    });
}
function ClientsListViewWithReferenceFields(param) {
    let { searchDebounceMs } = param;
    const referenceFields = useClientReferenceFieldsData();
    return /*#__PURE__*/ _jsx(ClientsListView, {
        searchDebounceMs: searchDebounceMs,
        referenceFields: referenceFields
    });
}
export function ClientsPage(param) {
    let { searchDebounceMs = CLIENTS_SEARCH_DEBOUNCE_MS } = param;
    const detailMatch = useMatch({
        path: routes.client(),
        end: false
    });
    const { CLIENT_REFERENCE_FIELD_COLUMNS } = useFeatureFlags();
    if (detailMatch != null) {
        return /*#__PURE__*/ _jsx(ClientDetailPage, {
            clientId: detailMatch.params.clientId
        });
    }
    return CLIENT_REFERENCE_FIELD_COLUMNS ? /*#__PURE__*/ _jsx(ClientsListViewWithReferenceFields, {
        searchDebounceMs: searchDebounceMs
    }) : /*#__PURE__*/ _jsx(ClientsListView, {
        searchDebounceMs: searchDebounceMs
    });
}
